import React, { useEffect, useRef, useState } from "react";
import {
  Button, Card, Col, DatePicker, Form, Input, InputNumber,
  Modal, Popconfirm, Row, Select, Space, Table, Tag, Typography, message,
} from "antd";
import {
  ArrowLeftOutlined, PlusOutlined, MinusCircleOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const API_URL = process.env.REACT_APP_API_URL;

const TIPOS_EVENTO = [
  { value: "Consulta",        label: "Consulta veterinaria" },
  { value: "Vacuna",          label: "Vacuna" },
  { value: "Antiparasitario", label: "Antiparasitario" },
  { value: "Vitamina",        label: "Vitamina / suplemento" },
  { value: "Desparasitacion", label: "Desparasitación" },
  { value: "Otro",            label: "Otro" },
];

const COLOR_TIPO = {
  Consulta:        "blue",
  Vacuna:          "green",
  Antiparasitario: "orange",
  Vitamina:        "purple",
  Desparasitacion: "cyan",
  Otro:            "default",
};

export default function FrmSalud() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [eventos, setEventos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editandoId, setEditandoId] = useState(null);

  const [productos, setProductos] = useState([]);

  // ✅ FIX: Ref para evitar doble submit (el estado de React es asíncrono
  // y puede permitir un segundo clic antes de que setGuardando(true) surta efecto)
  const guardandoRef = useRef(false);

  const cargarEventos = async () => {
    try {
      setCargando(true);
      const resp = await axios.get(`${API_URL}/SaludEvento/animal/${id}`);
      const lista = Array.isArray(resp.data) ? resp.data
        : Array.isArray(resp.data?.$values) ? resp.data.$values : [];
      setEventos(lista);
    } catch {
      message.error("Error cargando eventos de salud.");
    } finally {
      setCargando(false);
    }
  };

  const cargarProductos = async () => {
    try {
      const resp = await axios.get(`${API_URL}/Producto`);
      const lista = Array.isArray(resp.data) ? resp.data
        : Array.isArray(resp.data?.$values) ? resp.data.$values : [];
      setProductos(lista);
    } catch (err) {
      message.error("No se pudo cargar la lista de productos del inventario.");
      console.error("cargarProductos error:", err);
    }
  };

  useEffect(() => {
    cargarEventos();
    cargarProductos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const abrirModal = (evento = null) => {
    form.resetFields();
    if (evento) {
      setEditandoId(evento.id);
      form.setFieldsValue({
        fecha: dayjs(evento.fecha),
        tipoEvento: evento.tipoEvento ?? "Consulta",
        diagnostico: evento.diagnostico ?? "",
        veterinario: evento.veterinario ?? "",
        costo: evento.costo ?? null,
        finCarenciaLeche: evento.finCarenciaLeche ? dayjs(evento.finCarenciaLeche) : null,
        finCarenciaCarne: evento.finCarenciaCarne ? dayjs(evento.finCarenciaCarne) : null,
        observacion: evento.observacion ?? "",
        tratamientos: (
          Array.isArray(evento.tratamientos)
            ? evento.tratamientos
            : (evento.tratamientos?.$values ?? [])
        ).map(t => ({
          productoId: t.productoId,
          dosis: t.dosis,
          unidad: t.unidad ?? "",
          via: t.via ?? "",
          duracionDias: t.duracionDias ?? null,
          // ✅ FIX: aplicacionesDia ahora viene de la BD (ya se persiste)
          aplicacionesDia: t.aplicacionesDia ?? 1,
          frecuencia: t.frecuencia ?? "",
          cantidadInventarioUsada: t.cantidadInventarioUsada ?? (t.dosis ?? 0),
          lote: t.lote ?? "",
        })),
      });
    } else {
      setEditandoId(null);
      form.setFieldsValue({ fecha: dayjs(), tipoEvento: "Consulta", tratamientos: [] });
    }
    setModalOpen(true);
  };

  const recalcularTotal = (idx, cambio) => {
    const tratos = form.getFieldValue("tratamientos") || [];
    if (tratos[idx] === undefined) return;
    const t = { ...tratos[idx], ...cambio };
    const dosis = t.dosis ?? 0;
    const dias = t.duracionDias ?? 1;
    const apxDia = t.aplicacionesDia ?? 1;
    const total = parseFloat((dosis * dias * apxDia).toFixed(3));
    const updated = [...tratos];
    updated[idx] = { ...t, cantidadInventarioUsada: total };
    form.setFieldsValue({ tratamientos: updated });
  };

  const onGuardar = async () => {
    // ✅ FIX: Bloqueo inmediato con ref para evitar doble submit.
    // setGuardando es asíncrono (React batching) — el ref garantiza bloqueo sincrónico.
    if (guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardando(true);

    try {
      const values = await form.validateFields();

      const payload = {
        animalId: Number(id),
        fecha: values.fecha.toISOString(),
        tipoEvento: values.tipoEvento ?? "Consulta",
        diagnostico: values.diagnostico || null,
        observacion: values.observacion || null,
        veterinario: values.veterinario || null,
        costo: values.costo ?? null,
        finCarenciaLeche: values.finCarenciaLeche ? values.finCarenciaLeche.toISOString() : null,
        finCarenciaCarne: values.finCarenciaCarne ? values.finCarenciaCarne.toISOString() : null,
        tratamientos: (values.tratamientos ?? []).map(t => ({
          productoId: t.productoId,
          dosis: t.dosis ?? 0,
          unidad: t.unidad || null,
          via: t.via || null,
          duracionDias: t.duracionDias ?? null,
          aplicacionesDia: t.aplicacionesDia ?? 1,   // ✅ FIX: ahora se persiste en la BD
          frecuencia: t.frecuencia || null,
          cantidadInventarioUsada: t.cantidadInventarioUsada ?? 0,
          lote: t.lote || null,
        })),
      };

      if (editandoId) {
        await axios.put(`${API_URL}/SaludEvento/${editandoId}`, payload);
        message.success("Evento actualizado.");
      } else {
        await axios.post(`${API_URL}/SaludEvento`, payload);
        message.success("Evento registrado.");
      }

      setModalOpen(false);
      cargarEventos();
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Error guardando el evento.");
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  const onEliminar = async (eventoId) => {
    try {
      await axios.delete(`${API_URL}/SaludEvento/${eventoId}`);
      message.success("Evento eliminado.");
      cargarEventos();
    } catch {
      message.error("Error eliminando el evento.");
    }
  };

  const columns = [
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      render: (v) => dayjs(v).format("YYYY-MM-DD"),
      sorter: (a, b) => dayjs(a.fecha).valueOf() - dayjs(b.fecha).valueOf(),
      defaultSortOrder: "descend",
    },
    {
      title: "Tipo",
      dataIndex: "tipoEvento",
      key: "tipoEvento",
      render: (v) => (
        <Tag color={COLOR_TIPO[v] ?? "default"}>
          {TIPOS_EVENTO.find(t => t.value === v)?.label ?? v}
        </Tag>
      ),
    },
    {
      title: "Diagnóstico / Detalle",
      dataIndex: "diagnostico",
      key: "diagnostico",
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Veterinario",
      dataIndex: "veterinario",
      key: "veterinario",
      responsive: ["md"],
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Costo",
      dataIndex: "costo",
      key: "costo",
      responsive: ["md"],
      align: "right",
      render: (v) => v != null ? `$${Number(v).toLocaleString()}` : <Text type="secondary">—</Text>,
    },
    {
      title: "Carencia leche",
      dataIndex: "finCarenciaLeche",
      key: "finCarenciaLeche",
      responsive: ["lg"],
      render: (v) => {
        if (!v) return <Text type="secondary">—</Text>;
        const fecha = dayjs(v);
        const vencida = fecha.isBefore(dayjs());
        return (
          <Tag color={vencida ? "default" : "warning"}>
            {fecha.format("YYYY-MM-DD")}
          </Tag>
        );
      },
    },
    {
      title: "",
      key: "acciones",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => abrirModal(record)}>Editar</Button>
          <Popconfirm
            title="¿Eliminar este evento?"
            okText="Sí"
            cancelText="No"
            onConfirm={() => onEliminar(record.id)}
          >
            <Button size="small" danger>Eliminar</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Volver
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          <MedicineBoxOutlined style={{ marginRight: 8 }} />
          Historia Clínica
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => abrirModal()}>
          Nuevo evento
        </Button>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={eventos}
        loading={cargando}
        scroll={{ x: "max-content" }}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        expandable={{
          expandedRowRender: (record) => {
            const tratos = Array.isArray(record.tratamientos) ? record.tratamientos
              : Array.isArray(record.tratamientos?.$values) ? record.tratamientos.$values : [];
            return (
              <Card size="small" title="Tratamientos / Medicamentos aplicados">
                {tratos.length === 0 ? (
                  <Text type="secondary">Sin tratamientos registrados.</Text>
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={tratos}
                    columns={[
                      { title: "Producto / Medicamento", dataIndex: "nombreProducto", key: "nombreProducto", render: v => v || <Text type="secondary">Sin nombre</Text> },
                      { title: "Dosis / aplic.", dataIndex: "dosis", key: "dosis", render: (v, r) => `${v} ${r.unidad || ""}`.trim() },
                      { title: "Vía", dataIndex: "via", key: "via", render: v => v || "—" },
                      { title: "Duración (días)", dataIndex: "duracionDias", key: "duracionDias", render: v => v ?? "—" },
                      { title: "Frecuencia", dataIndex: "frecuencia", key: "frecuencia", render: v => v || "—" },
                      { title: "Desc. inventario", dataIndex: "cantidadInventarioUsada", key: "cantidadInventarioUsada",
                        render: (v, r) => v > 0 ? <Tag color="red">-{v} {r.unidad || ""}</Tag> : <Text type="secondary">0</Text> },
                      { title: "Lote", dataIndex: "lote", key: "lote", render: v => v || "—" },
                    ]}
                  />
                )}
                {record.observacion && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>Observación: </Text>
                    <Text>{record.observacion}</Text>
                  </div>
                )}
              </Card>
            );
          },
          rowExpandable: (record) => {
            const tratos = Array.isArray(record.tratamientos) ? record.tratamientos
              : Array.isArray(record.tratamientos?.$values) ? record.tratamientos.$values : [];
            return tratos.length > 0 || !!record.observacion;
          },
        }}
      />

      <Modal
        title={editandoId ? "Editar evento de salud" : "Registrar evento de salud"}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditandoId(null); }}
        onOk={onGuardar}
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={guardando}
        destroyOnClose
        width={800}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Fecha"
                name="fecha"
                rules={[{ required: true, message: "La fecha es obligatoria." }]}
              >
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Tipo de evento"
                name="tipoEvento"
                rules={[{ required: true, message: "Seleccione el tipo." }]}
              >
                <Select placeholder="Seleccione tipo">
                  {TIPOS_EVENTO.map(t => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Costo ($)" name="costo">
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder="Ej: 50000"
                  formatter={v => v ? `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
                  parser={v => v.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Diagnóstico / Descripción" name="diagnostico">
                <Input placeholder="Ej: Mastitis, Vacuna aftosa, Vitamina ADE..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Veterinario" name="veterinario">
                <Input placeholder="Nombre del veterinario (opcional)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Fin carencia leche" name="finCarenciaLeche">
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Fin carencia carne" name="finCarenciaCarne">
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Observaciones" name="observacion">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>

          {/* ── Tratamientos / Medicamentos ── */}
          <div style={{ marginTop: 8, marginBottom: 4 }}>
            <Text strong>Tratamientos / Medicamentos aplicados</Text>
          </div>

          <Form.List name="tratamientos">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    style={{ marginBottom: 8, background: "#fafafa" }}
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                      >
                        Quitar
                      </Button>
                    }
                  >
                    <Row gutter={[8, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          {...restField}
                          label="Producto / Medicamento"
                          name={[name, "productoId"]}
                          rules={[{ required: true, message: "Seleccione el producto." }]}
                        >
                          <Select
                            showSearch
                            placeholder="Buscar producto..."
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                          >
                            {productos.map(p => (
                              <Option key={p.id} value={p.id}>{p.nombre}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item
                          {...restField}
                          label="Dosis por aplicación"
                          name={[name, "dosis"]}
                          rules={[{ required: true, message: "Ingrese la dosis." }]}
                        >
                          <InputNumber
                            min={0}
                            step={0.1}
                            style={{ width: "100%" }}
                            placeholder="Ej: 15"
                            onChange={val => recalcularTotal(name, { dosis: val })}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} label="Unidad" name={[name, "unidad"]}>
                          <Input placeholder="ml, g, cc..." />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} label="Vía" name={[name, "via"]}>
                          <Input placeholder="IM, IV, SC, oral..." />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} label="Duración (días)" name={[name, "duracionDias"]}>
                          <InputNumber
                            min={1}
                            style={{ width: "100%" }}
                            onChange={val => recalcularTotal(name, { duracionDias: val })}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} label="Aplicaciones por día" name={[name, "aplicacionesDia"]}
                          tooltip="Cuántas veces al día se aplica: 1 = una vez, 2 = cada 12h, 3 = cada 8h, etc.">
                          <InputNumber
                            min={1}
                            max={24}
                            style={{ width: "100%" }}
                            placeholder="Ej: 2"
                            onChange={val => recalcularTotal(name, { aplicacionesDia: val })}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} label="Frecuencia (descripción)" name={[name, "frecuencia"]}>
                          <Input placeholder="Cada 12h, una vez..." />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item
                          {...restField}
                          label="Total a descontar del inventario"
                          name={[name, "cantidadInventarioUsada"]}
                          tooltip="= dosis × aplicaciones/día × días. Puede editarlo manualmente."
                          rules={[{ required: true, message: "Indique la cantidad a descontar." }]}
                        >
                          <InputNumber
                            min={0}
                            step={0.1}
                            style={{ width: "100%", borderColor: "#faad14" }}
                            placeholder="Se calcula automáticamente"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item {...restField} label="Lote (opcional)" name={[name, "lote"]}>
                          <Input placeholder="Número de lote del producto" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}

                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Agregar tratamiento / medicamento
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}
